import { useEffect, useRef, useState } from "react";

const DEFAULT_MIN_DISPLAY_MS = 500;

/**
 * Returns a boolean that stays `true` for at least `minDurationMs` once
 * `isLoading` first becomes true. Prevents loading indicators from flashing
 * briefly on fast loads.
 *
 * The show path uses React's derived-state-during-render pattern so the
 * indicator is present before the browser's first paint — preventing the
 * visible flash of underlying content that occurs on high-contrast accessible
 * themes when an effect-based approach introduces a one-tick delay.
 */
export function useMinDurationLoading(
  isLoading: boolean,
  minDurationMs: number = DEFAULT_MIN_DISPLAY_MS,
): boolean {
  const [prevIsLoading, setPrevIsLoading] = useState(isLoading);
  const [shouldShow, setShouldShow] = useState(isLoading);
  const showTimeRef = useRef<number | null>(null);

  // Derived state update during render — shows the indicator immediately when
  // isLoading transitions to true, before the browser paints. Calling setState
  // here causes React to discard this render and immediately re-render with the
  // updated state (no effect delay, no visible flash).
  if (isLoading && !prevIsLoading) {
    setPrevIsLoading(true);
    setShouldShow(true);
  } else if (!isLoading && prevIsLoading) {
    setPrevIsLoading(false);
    // shouldShow is cleared by the effect below after the minimum duration.
  }

  // Record the start time (performance.now is impure; safe to call in effects)
  // and schedule the hide after the minimum duration has elapsed.
  useEffect(() => {
    if (isLoading) {
      showTimeRef.current = performance.now();
    } else if (showTimeRef.current !== null) {
      const elapsed = performance.now() - showTimeRef.current;
      const remaining = Math.max(0, minDurationMs - elapsed);
      const timerId = setTimeout(() => {
        setShouldShow(false);
        showTimeRef.current = null;
      }, remaining);
      return () => clearTimeout(timerId);
    }
  }, [isLoading, minDurationMs]);

  return shouldShow;
}
